import { css, styled } from 'styled-components';

import { ButtonOrLink } from '#/components/ButtonOrLink/ButtonOrLink';
import { IdealistLogoTextImg } from '#/components/Logo/IdealistLogo.styled';
import { visuallyHiddenCss } from '#/components/VisuallyHidden/VisuallyHidden';
import { scrolledDownHiddenVar, scrolledDownZeroVar } from '#/theme/scrolled';
import {
  colors,
  cssBreakpoints,
  gutterWidth,
  h3Text,
  transition,
} from '#/theme/theme';

import { layoutHeaderMobileLinksHeight } from './layoutHeaderConstants';

export const LayoutHeaderHolder = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 101;

  display: flex;
  flex-direction: column;
  background: ${colors.white};
  box-shadow: 0 2px 4px rgb(0 0 0 / 0.075);
  font-size: 15px;
  line-height: normal;

  @media print {
    display: none;
  }
`;

export const LayoutHeaderTopSectionHolder = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${gutterWidth / 2}px;
  height: 58px;
  position: relative;
`;

export const LayoutHeaderLeft = styled.div`
  display: flex;
  align-items: center;

  &[data-collapsible] {
    @media (max-width: ${cssBreakpoints.mdDown}) {
      flex: 1;
      min-width: 0;
    }
  }
`;

const logoLinkMarginRight = 24;

// Mark (28px) + gap (7px) + wordmark (58px) at the 28px mobile logo height
const logoWithWordmarkMinWidth = 93 + logoLinkMarginRight;

/*
 * NOTE: we wrap the logo only, so the mobile links row (control bucket,
 * absolutely positioned) stays outside the container, i.e. a separate
 * stacking context.
 */
export const LayoutHeaderLogoHolder = styled.div`
  display: flex;

  ${LayoutHeaderLeft}[data-collapsible] & {
    @media (max-width: ${cssBreakpoints.mdDown}) {
      flex: 1;
      min-width: 0;
      container-type: inline-size;
      container-name: layout-header-logo;
    }
  }

  /* TODO(@paprikka): PM to sign off wordmark collapse on narrow CMS posts, sc-68810 */
  @container layout-header-logo (width < ${logoWithWordmarkMinWidth}px) {
    ${IdealistLogoTextImg} {
      display: none;
    }
  }
`;

export const LayoutHeaderLinksHolder = styled.div<{
  $hideMobileHeaderOnScroll: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: ${cssBreakpoints.mdDown}) {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    height: ${layoutHeaderMobileLinksHeight}px;
    border-top: 1px solid ${colors.selectionGrey};
    justify-content: space-evenly;
    gap: 0;
  }

  /* Hamburger menu enabled: it replaces this row */
  &[data-hidden-on-mobile] {
    @media (max-width: ${cssBreakpoints.mdDown}) {
      display: none;
    }
  }

  ${(props) =>
    props.$hideMobileHeaderOnScroll &&
    css`
      @media all and (max-width: ${cssBreakpoints.mdDown}) {
        transition: height ${transition};
        height: calc(
          ${layoutHeaderMobileLinksHeight}px * var(${scrolledDownZeroVar}, 1)
        );
        overflow: var(${scrolledDownHiddenVar});
      }
    `};
`;

// Only display the API & Integrations button on desktop up to a certain point
// This is a magical number - it was just a rough estimate of what fits in the current UI
const apiIntegrationsDropdownCutoffPoint = '1140px';

export const LayoutHeaderApiAndIntegrationsDropdownHolder = styled.div`
  @media (max-width: ${apiIntegrationsDropdownCutoffPoint}) {
    display: none;
  }
`;

export const LayoutHeaderDesktopDonateLinkHolder = styled.div`
  @media (max-width: ${cssBreakpoints.mdDown}) {
    display: none;
  }
`;

export const LayoutHeaderRight = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const LayoutHeaderLogoLink = styled(ButtonOrLink)`
  display: inline-flex;
  margin-right: ${logoLinkMarginRight}px;
`;

export const LayoutHeaderAuthLink = styled(ButtonOrLink)`
  padding: 0 4px;
  color: ${colors.mediumContentGrey};
  font-size: inherit;
  transition: color ${transition};

  &,
  &:hover,
  &:focus {
    text-decoration: none;
  }

  &:hover,
  &:focus {
    color: ${colors.brandBlue};
  }
`;

const layoutHeaderAuthHolderDesktopGap = 16;
export const LayoutHeaderAuthHolder = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${layoutHeaderAuthHolderDesktopGap}px;

  @media (max-width: ${cssBreakpoints.mdUp}) {
    margin-right: ${layoutHeaderAuthHolderDesktopGap / 2}px;
  }

  a {
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      right: -${layoutHeaderAuthHolderDesktopGap / 2}px;
      transform: translateY(-50%);
      width: 1px;
      height: 20px;
      background: ${colors.elementGrey};
      opacity: 0.5;
    }

    @media (min-width: ${cssBreakpoints.mdUp}) {
      &:last-child {
        &::before {
          display: none;
        }
      }
    }
  }
`;

export const LayoutHeaderSignupLinkHolder = styled.div`
  @media all and (max-width: 320px) {
    display: none;
  }
`;

export const LayoutHeaderFocusedTitle = styled.h3`
  ${h3Text};
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: ${cssBreakpoints.smDown}) {
    ${visuallyHiddenCss};
  }
`;

export const LayoutFocusedHeaderSubmenuHolder = styled.div`
  height: 50px;
  border-top: 1px solid ${colors.selectionGrey};
`;

export const LayoutHeaderProgressBarHolder = styled.div`
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 1;
`;
